'use client';

import { useEffect, useMemo, useRef, useState, useTransition } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  Bug,
  Camera,
  LifeBuoy,
  Lightbulb,
  Loader2,
  Paperclip,
  Upload,
  X,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { useFileDrop } from '@kit/ui/hooks/use-file-drop';
import { Input } from '@kit/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { toast } from '@kit/ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@kit/ui/tabs';
import { Textarea } from '@kit/ui/textarea';
import { cn } from '@kit/ui/utils';

import { submitBugReportAction } from '../../_lib/server/bug-report-actions';
import {
  captureScreenAsFile,
  isScreenCaptureSupported,
  isUserCancellation,
} from './capture-screen';
import { compressImageIfNeeded } from './compress-image';
import { MyReportsList } from './my-reports-list';
import {
  BUG_REPORT_ACCEPTED_MIME,
  getAttachmentExtension,
} from './report-labels';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB — matches the bucket's file_size_limit
const ACCEPTED_MIME: readonly string[] = BUG_REPORT_ACCEPTED_MIME;

const BugReportFormSchema = z.object({
  reportType: z.enum(['bug', 'feature_request', 'support_request']),
  title: z.string().trim().min(3, 'Title is too short').max(255),
  severity: z.enum(['low', 'medium', 'high']),
  description: z
    .string()
    .trim()
    .min(10, 'Please describe it in more detail')
    .max(10_000),
});

type BugReportFormValues = z.infer<typeof BugReportFormSchema>;

interface BugReportDialogProps {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Deep-linked report (?bugReport=<id>): opens on "My reports" with this thread expanded. */
  initialReportId?: string | null;
}

// Captured once per submission so triage doesn't have to ask "what device /
// browser / screen size were you on?" for every report.
function captureClientContext() {
  return {
    userAgent: navigator.userAgent.slice(0, 512),
    language: navigator.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

export function BugReportDialog({
  accountId,
  open,
  onOpenChange,
  initialReportId,
}: BugReportDialogProps) {
  const { t, i18n } = useTranslation('common');
  const supabase = useSupabase();
  const queryClient = useQueryClient();
  // The provider resolves the deep link synchronously (lazy initializer), so
  // the prop is already set on first render — no effect needed to follow it.
  const [activeTab, setActiveTab] = useState<'new' | 'mine'>(
    initialReportId ? 'mine' : 'new',
  );
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, startTransition] = useTransition();
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureSupported, setCaptureSupported] = useState(false);

  useEffect(() => {
    // navigator is undefined during SSR; defer the check to after mount so
    // the button doesn't flash in and back out.
    setCaptureSupported(isScreenCaptureSupported());
  }, []);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Attachment slots claimed by in-flight handleFiles calls. See handleFiles:
  // it is the shared ledger that keeps concurrent picker/drop/paste additions
  // from each independently filling up to MAX_FILES.
  const reservedSlots = useRef(0);

  const form = useForm<BugReportFormValues>({
    resolver: zodResolver(BugReportFormSchema),
    defaultValues: {
      reportType: 'bug',
      title: '',
      severity: 'low',
      description: '',
    },
  });

  const reportType = form.watch('reportType');
  const isFeature = reportType === 'feature_request';
  const isSupport = reportType === 'support_request';
  // Feature and support requests both use "Priority" (how badly the reporter
  // needs it); only bugs use "Severity".
  const usesPriority = reportType !== 'bug';

  const handleFiles = async (incoming: FileList | File[] | null) => {
    if (!incoming) return;

    // Capacity is tracked in a ref, not from `files.length`, because there are
    // now three concurrent entry points (picker, drop, paste) and the loop
    // awaits compression. Two overlapping calls would both read the same stale
    // length, each accept up to MAX_FILES, and the later setFiles would slice
    // the other's files away with no explanation. The ref is the shared
    // reservation ledger, so the second call sees what the first already took.
    reservedSlots.current = Math.max(reservedSlots.current, files.length);
    const accepted: File[] = [];

    for (const file of Array.from(incoming)) {
      if (reservedSlots.current >= MAX_FILES) {
        toast.error(
          t('bugReport.errors.maxAttachments', {
            defaultValue: 'You can attach at most {{count}} attachments',
            count: MAX_FILES,
          }),
        );
        break;
      }

      if (!ACCEPTED_MIME.includes(file.type)) {
        toast.error(
          t('bugReport.errors.unsupportedFileType', {
            defaultValue:
              '{{fileName}}: only images, MP4 videos, PDF, or Word documents are allowed',
            fileName: file.name,
          }),
        );
        continue;
      }

      const compressed = await compressImageIfNeeded(file);

      if (compressed.size > MAX_FILE_SIZE) {
        toast.error(
          t('bugReport.errors.fileTooLarge', {
            defaultValue: '{{fileName}}: exceeds the 50 MB limit',
            fileName: file.name,
          }),
        );
        continue;
      }

      accepted.push(compressed);
      reservedSlots.current += 1;
    }

    if (accepted.length > 0) {
      setFiles((prev) => [...prev, ...accepted].slice(0, MAX_FILES));
    }
  };

  const handleCaptureScreen = async () => {
    if (isCapturing || isSubmitting || files.length >= MAX_FILES) return;

    setIsCapturing(true);
    // Hide the dialog so it doesn't appear in the screenshot of this tab.
    onOpenChange(false);

    // Yield one frame so the dialog is actually unmounted before the picker
    // is shown. Without this, Chrome can still grab the dialog DOM.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );

    try {
      const file = await captureScreenAsFile();
      // Run the captured file through the existing validator so the 5-file
      // and 50-MB limits are enforced uniformly. handleFiles also compresses.
      const dt = new DataTransfer();
      dt.items.add(file);
      await handleFiles(dt.files);
    } catch (error) {
      if (!isUserCancellation(error)) {
        toast.error(
          t('bugReport.errors.captureFailed', {
            defaultValue:
              'Screen capture failed. Please attach a file instead.',
          }),
        );
      }
    } finally {
      setIsCapturing(false);
      onOpenChange(true);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      // Give the slot back, or the composer would stay "full" after a removal.
      reservedSlots.current = next.length;
      return next;
    });
  };

  // Object URLs for image thumbnails. Tracked in a ref-style state so we
  // can revoke each URL exactly once when its file is removed or the
  // dialog resets. Non-image files don't get an entry.
  const previewUrls = useMemo(() => {
    const map = new Map<File, string>();
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        map.set(file, URL.createObjectURL(file));
      }
    }
    return map;
  }, [files]);

  useEffect(() => {
    // Revoke when this set of URLs is replaced (file added/removed) or on unmount.
    return () => {
      for (const url of previewUrls.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, [previewUrls]);

  const dropZoneDisabled = isSubmitting || files.length >= MAX_FILES;

  const {
    dropProps,
    pasteProps,
    isDragOver,
    reset: resetDragState,
  } = useFileDrop({
    onFiles: handleFiles,
    disabled: dropZoneDisabled,
  });

  const reset = () => {
    form.reset();
    setFiles([]);
    reservedSlots.current = 0;
    resetDragState();
    setActiveTab('new');
  };

  const openFilePicker = () => {
    if (dropZoneDisabled) return;
    fileInputRef.current?.click();
  };

  const onSubmit = (values: BugReportFormValues) => {
    startTransition(async () => {
      try {
        const reportId = randomId();

        // Upload all screenshots concurrently — Promise.all preserves array
        // order, and the first rejection propagates to the catch below.
        const uploadedPaths = await Promise.all(
          files.map(async (file) => {
            const extension = getAttachmentExtension(file);
            const filename = `${randomId()}${extension}`;
            const path = `${accountId}/${reportId}/${filename}`;

            const { error } = await supabase.storage
              .from('bug_reports')
              .upload(path, file, {
                upsert: false,
                contentType: file.type,
              });

            if (error) {
              throw new Error(
                `Failed to upload ${file.name}: ${error.message}`,
              );
            }

            return path;
          }),
        );

        const result = await submitBugReportAction({
          accountId,
          reportType: values.reportType,
          title: values.title,
          description: values.description,
          severity: values.severity,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          screenshots: uploadedPaths,
          context: captureClientContext(),
        });

        if (!result?.success) {
          throw new Error('Server rejected the report');
        }

        toast.success(
          values.reportType === 'feature_request'
            ? t('bugReport.toasts.featureRequestSent', {
                defaultValue: 'Thanks — your feature request has been sent',
              })
            : values.reportType === 'support_request'
              ? t('bugReport.toasts.supportRequestSent', {
                  defaultValue: 'Thanks — your support request has been sent',
                })
              : t('bugReport.toasts.bugReportSent', {
                  defaultValue: 'Thanks — your bug report has been sent',
                }),
        );

        // Refresh the "My reports" tab so the new report shows up even if
        // the list stayed mounted from a previous open.
        void queryClient.invalidateQueries({
          queryKey: ['my-bug-reports', accountId],
        });

        reset();
        onOpenChange(false);
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : t('bugReport.errors.submitFailed', {
                defaultValue: 'Failed to submit report',
              });
        toast.error(message);
      }
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting) return;
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="z-[9000] flex max-h-[85dvh] w-[calc(100%-2rem)] max-w-4xl flex-col gap-5 overflow-hidden rounded-2xl p-4 sm:p-6"
      >
        <DialogHeader className="shrink-0 pe-8">
          <DialogTitle className="text-xl leading-snug sm:text-2xl">
            {t('bugReport.title', {
              defaultValue: 'Report a bug or request a feature',
            })}
          </DialogTitle>
        </DialogHeader>

        <Tabs
          dir={i18n.dir()}
          className="flex min-h-0 min-w-0 flex-col"
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as 'new' | 'mine')}
        >
          <TabsList variant="underline" className="w-full shrink-0">
            <TabsTrigger
              value="new"
              disabled={isSubmitting}
              className="min-h-11 text-base"
            >
              {t('bugReport.tabs.new', { defaultValue: 'New report' })}
            </TabsTrigger>
            <TabsTrigger
              value="mine"
              disabled={isSubmitting}
              className="min-h-11 text-base"
            >
              {t('bugReport.tabs.mine', { defaultValue: 'My reports' })}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mine" className="min-h-0 overflow-y-auto overscroll-contain px-1 pt-3 pb-1">
            <MyReportsList
              accountId={accountId}
              highlightReportId={initialReportId ?? null}
            />
          </TabsContent>

          <TabsContent value="new" className="min-h-0 overflow-y-auto overscroll-contain px-1 pb-1">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4 py-2"
              >
                <FormField
                  control={form.control}
                  name="reportType"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div
                          className="grid grid-cols-1 gap-2 sm:grid-cols-3"
                          role="radiogroup"
                        >
                          <ReportTypeOption
                            icon={
                              <Bug className="text-muted-foreground h-4 w-4" />
                            }
                            label={t('bugReport.type.bug.label', {
                              defaultValue: 'Bug',
                            })}
                            hint={t('bugReport.type.bug.hint', {
                              defaultValue: "Something isn't working",
                            })}
                            selected={field.value === 'bug'}
                            disabled={isSubmitting}
                            onSelect={() => field.onChange('bug')}
                          />
                          <ReportTypeOption
                            icon={
                              <Lightbulb className="text-muted-foreground h-4 w-4" />
                            }
                            label={t('bugReport.type.feature.label', {
                              defaultValue: 'Feature request',
                            })}
                            hint={t('bugReport.type.feature.hint', {
                              defaultValue: 'An idea or improvement',
                            })}
                            selected={field.value === 'feature_request'}
                            disabled={isSubmitting}
                            onSelect={() => field.onChange('feature_request')}
                          />
                          <ReportTypeOption
                            icon={
                              <LifeBuoy className="text-muted-foreground h-4 w-4" />
                            }
                            label={t('bugReport.type.support.label', {
                              defaultValue: 'Support request',
                            })}
                            hint={t('bugReport.type.support.hint', {
                              defaultValue: 'Assistance or a question',
                            })}
                            selected={field.value === 'support_request'}
                            disabled={isSubmitting}
                            onSelect={() => field.onChange('support_request')}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('bugReport.fields.title.label', {
                          defaultValue: 'Title',
                        })}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={
                            isFeature
                              ? t('bugReport.fields.title.placeholderFeature', {
                                  defaultValue: 'Short summary of your idea',
                                })
                              : isSupport
                                ? t(
                                    'bugReport.fields.title.placeholderSupport',
                                    {
                                      defaultValue:
                                        'What do you need help with?',
                                    },
                                  )
                                : t('bugReport.fields.title.placeholderBug', {
                                    defaultValue: 'Short summary of the issue',
                                  })
                          }
                          className="text-base md:text-base"
                          maxLength={255}
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="severity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {usesPriority
                          ? t('bugReport.fields.severity.labelFeature', {
                              defaultValue: 'Priority',
                            })
                          : t('bugReport.fields.severity.label', {
                              defaultValue: 'Severity',
                            })}
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isSubmitting}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                usesPriority
                                  ? t(
                                      'bugReport.fields.severity.placeholderFeature',
                                      { defaultValue: 'Select priority' },
                                    )
                                  : t('bugReport.fields.severity.placeholder', {
                                      defaultValue: 'Select severity',
                                    })
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        {/* The dialog sits at z-[9000]; SelectContent's own
                            !z-[10002] already clears it (and stays below the
                            tour layer at z-[10050]), so no override is needed
                            here. */}
                        <SelectContent>
                          <SelectItem value="low">
                            {usesPriority
                              ? t('bugReport.fields.severity.lowFeature', {
                                  defaultValue: 'Low — nice to have',
                                })
                              : t('bugReport.fields.severity.low', {
                                  defaultValue: 'Low — minor issue',
                                })}
                          </SelectItem>
                          <SelectItem value="medium">
                            {usesPriority
                              ? t('bugReport.fields.severity.mediumFeature', {
                                  defaultValue:
                                    'Medium — would make a real difference',
                                })
                              : t('bugReport.fields.severity.medium', {
                                  defaultValue:
                                    'Medium — something is broken but has a workaround',
                                })}
                          </SelectItem>
                          <SelectItem value="high">
                            {usesPriority
                              ? t('bugReport.fields.severity.highFeature', {
                                  defaultValue:
                                    'High — I need this to run my business',
                                })
                              : t('bugReport.fields.severity.high', {
                                  defaultValue:
                                    'High — blocking me from working',
                                })}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('bugReport.fields.description.label', {
                          defaultValue: 'Description',
                        })}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          className="min-h-40 text-base leading-relaxed md:text-base"
                          placeholder={
                            isFeature
                              ? t(
                                  'bugReport.fields.description.placeholderFeature',
                                  {
                                    defaultValue:
                                      'What would you like to be able to do? What problem would it solve for you?',
                                  },
                                )
                              : isSupport
                                ? t(
                                    'bugReport.fields.description.placeholderSupport',
                                    {
                                      defaultValue:
                                        'What do you need help with? Describe your question or the assistance you need.',
                                    },
                                  )
                                : t(
                                    'bugReport.fields.description.placeholderBug',
                                    {
                                      defaultValue:
                                        'What did you expect to happen? What happened instead? Steps to reproduce?',
                                    },
                                  )
                          }
                          maxLength={10_000}
                          disabled={isSubmitting}
                          {...field}
                          {...pasteProps}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium">
                      {t('bugReport.attachments.heading', {
                        defaultValue: 'Attachments ({{count}}/{{max}})',
                        count: files.length,
                        max: MAX_FILES,
                      })}
                    </span>
                    {captureSupported ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCaptureScreen}
                        disabled={
                          isSubmitting ||
                          isCapturing ||
                          files.length >= MAX_FILES
                        }
                      >
                        {isCapturing ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Camera className="mr-2 h-4 w-4" />
                        )}
                        {t('bugReport.attachments.captureScreen', {
                          defaultValue: 'Capture screen',
                        })}
                      </Button>
                    ) : null}
                  </div>

                  <div
                    role="button"
                    tabIndex={dropZoneDisabled ? -1 : 0}
                    aria-label={t('bugReport.attachments.dropZoneLabel', {
                      defaultValue: 'Drop files here or click to browse',
                    })}
                    aria-disabled={dropZoneDisabled}
                    onClick={openFilePicker}
                    onKeyDown={(event) => {
                      if (dropZoneDisabled) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openFilePicker();
                      }
                    }}
                    {...dropProps}
                    className={
                      'focus-visible:ring-ring flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-7 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2' +
                      (dropZoneDisabled
                        ? ' border-border/50 bg-muted/20 text-muted-foreground/70 cursor-not-allowed'
                        : isDragOver
                          ? ' border-primary bg-primary/5 text-foreground cursor-copy'
                          : ' border-border/60 text-muted-foreground hover:border-border hover:bg-muted/30 cursor-pointer')
                    }
                  >
                    <Upload className="h-5 w-5" aria-hidden="true" />
                    <div className="text-sm font-medium">
                      {files.length >= MAX_FILES
                        ? t('bugReport.attachments.limitReached', {
                            defaultValue: 'Attachment limit reached ({{max}})',
                            max: MAX_FILES,
                          })
                        : isDragOver
                          ? t('bugReport.attachments.dropToAttach', {
                              defaultValue: 'Drop files to attach',
                            })
                          : t('bugReport.attachments.dragOrBrowse', {
                              defaultValue:
                                'Drag files here, paste a screenshot, or click to browse',
                            })}
                    </div>
                    <div className="text-xs">
                      {t('bugReport.attachments.hint', {
                        defaultValue:
                          'Images, MP4 videos, PDF, or Word documents. Up to {{max}} files, 50 MB each.',
                        max: MAX_FILES,
                      })}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_MIME.join(',')}
                      multiple
                      className="hidden"
                      disabled={dropZoneDisabled}
                      onChange={(event) => {
                        handleFiles(event.target.files);
                        event.target.value = '';
                      }}
                    />
                  </div>

                  {files.length > 0 ? (
                    <ul className="mt-2 space-y-1">
                      {files.map((file, index) => {
                        const previewUrl = previewUrls.get(file);
                        return (
                          <li
                            key={`${file.name}-${index}`}
                            className="bg-muted/30 border-border/60 flex items-center justify-between rounded-lg border px-3 py-2 text-sm"
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              {previewUrl ? (
                                <img
                                  src={previewUrl}
                                  alt={file.name}
                                  className="bg-background h-10 w-10 shrink-0 rounded border object-cover"
                                />
                              ) : (
                                <Paperclip className="text-muted-foreground h-4 w-4 shrink-0" />
                              )}
                              <span className="truncate">{file.name}</span>
                              <span className="text-muted-foreground text-xs">
                                {formatBytes(file.size)}
                              </span>
                            </span>
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground"
                              onClick={(event) => {
                                event.stopPropagation();
                                removeFile(index);
                              }}
                              disabled={isSubmitting}
                              aria-label={t('bugReport.attachments.remove', {
                                defaultValue: 'Remove {{fileName}}',
                                fileName: file.name,
                              })}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>

                <DialogFooter className="gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={isSubmitting || isCapturing}
                  >
                    {t('bugReport.actions.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                  <Button type="submit" disabled={isSubmitting || isCapturing}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('bugReport.actions.sending', {
                          defaultValue: 'Sending…',
                        })}
                      </>
                    ) : isFeature ? (
                      t('bugReport.actions.sendFeatureRequest', {
                        defaultValue: 'Send feature request',
                      })
                    ) : isSupport ? (
                      t('bugReport.actions.sendSupportRequest', {
                        defaultValue: 'Send support request',
                      })
                    ) : (
                      t('bugReport.actions.sendBugReport', {
                        defaultValue: 'Send bug report',
                      })
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function ReportTypeOption({
  icon,
  label,
  hint,
  selected,
  disabled,
  onSelect,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex flex-col items-start gap-1 rounded-lg border px-3.5 py-3 text-start transition-colors',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        selected
          ? 'border-primary bg-primary/5'
          : 'border-border/60 hover:border-border hover:bg-muted/40',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <span className="flex items-center gap-2 text-sm font-medium">
        {icon}
        {label}
      </span>
      <span className="text-muted-foreground text-xs">{hint}</span>
    </button>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function randomId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
